/**
 * TaskTable — vue tableau (style Sage/Acomba) : cocher pour terminer, statut modifiable en ligne, progression des étapes.
 */
import { BellRing, CheckCircle2, Circle, Copy, ListChecks, Plus, Repeat, Trash2 } from "lucide-react";
import { Btn, Empty, Pill } from "../comptabilite/_shared";
import { EXPERTISES, Progress, TASK_PRIORITIES, TASK_STATUSES, TASK_TYPES, dueInfo, meta } from "./taskHelpers";

export default function TaskTable({ tasks, onOpen, onStatus, onDuplicate, onDelete, onNew, done }) {
  return (
    <div className="bg-white border rounded-xl overflow-hidden" data-testid="task-table">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
          <tr>
            <th className="w-10 px-3 py-2" />
            <th className="text-left px-3 py-2">Tâche</th>
            <th className="text-left px-3 py-2 hidden lg:table-cell">Dossier</th>
            <th className="text-left px-3 py-2 w-28 hidden md:table-cell">Priorité</th>
            <th className="text-left px-3 py-2 w-44">Échéance</th>
            <th className="text-left px-3 py-2 w-36 hidden md:table-cell">Étapes</th>
            <th className="text-left px-3 py-2 w-36">Statut</th>
            <th className="w-20 px-2 py-2" />
          </tr>
        </thead>
        <tbody className="divide-y">
          {tasks.map((t) => {
            const isDone = t.status === "terminee";
            const type = meta(TASK_TYPES, t.task_type);
            const due = dueInfo(t);
            return (
              <tr key={t.id} className={`cursor-pointer transition ${t.overdue ? "bg-rose-50/40 hover:bg-rose-50" : "hover:bg-emerald-50/40"} ${isDone ? "text-slate-400" : ""}`} onClick={() => onOpen(t)} data-testid={`task-row-${t.id}`}>
                <td className="px-3 py-2">
                  <button onClick={(e) => { e.stopPropagation(); onStatus(t, isDone ? "a_faire" : "terminee"); }} title={isDone ? "Réouvrir" : "Marquer terminée"}
                    className={`transition ${isDone ? "text-emerald-600" : "text-slate-300 hover:text-emerald-600"}`} data-testid={`task-toggle-${t.id}`}>
                    {isDone ? <CheckCircle2 className="w-5 h-5" /> : <Circle className="w-5 h-5" />}
                  </button>
                </td>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <type.icon className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                    <span className={`font-medium ${isDone ? "line-through" : "text-slate-800"}`} data-testid={`task-title-${t.id}`}>{t.title}</span>
                    {t.task_type !== "tache" && <Pill>{type.label}</Pill>}
                    {t.recurrence_label && <span className="inline-flex items-center gap-0.5 text-[10px] text-violet-700" title={`Récurrence ${t.recurrence_label.toLowerCase()}${t.occurrence > 1 ? ` · occurrence n° ${t.occurrence}` : ""}`} data-testid={`task-recurring-${t.id}`}><Repeat className="w-3 h-3" /> {t.recurrence_label}</span>}
                    {!isDone && t.next_alert_at && <span className="inline-flex items-center gap-0.5 text-[10px] text-emerald-700" title={`Prochain rappel : ${new Date(t.next_alert_at).toLocaleString("fr-CA")}`} data-testid={`task-alert-badge-${t.id}`}><BellRing className="w-3 h-3" /> {(t.alerts || []).length}</span>}
                    {t.tax_year && <span className="text-[10px] text-slate-400">{t.tax_year}</span>}
                  </div>
                  {t.description && <div className="text-[11px] text-slate-400 truncate max-w-md">{t.description}</div>}
                  <div className="lg:hidden text-[11px] text-slate-500 mt-0.5">{t.master_name || "Sans dossier"}</div>
                </td>
                <td className="px-3 py-2 hidden lg:table-cell">
                  <div className="flex items-center gap-2">
                    <Pill cls={meta(EXPERTISES, t.expertise).cls}>{meta(EXPERTISES, t.expertise).label}</Pill>
                    <span className="text-xs text-slate-600 truncate max-w-[200px]">{t.master_number && <span className="font-mono text-teal-700 mr-1">{t.master_number}</span>}{t.master_name || <span className="text-slate-300">Sans dossier</span>}</span>
                  </div>
                </td>
                <td className="px-3 py-2 hidden md:table-cell"><Pill cls={meta(TASK_PRIORITIES, t.priority).cls}>{meta(TASK_PRIORITIES, t.priority).label}</Pill></td>
                <td className={`px-3 py-2 text-xs ${due.cls}`} data-testid={`task-due-${t.id}`}>{due.text}</td>
                <td className="px-3 py-2 hidden md:table-cell"><Progress done={t.steps_done} total={t.steps_total} /></td>
                <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                  <select value={t.status} onChange={(e) => onStatus(t, e.target.value)}
                    className={`text-[11px] font-semibold border rounded-full px-2 py-0.5 cursor-pointer focus:outline-none ${meta(TASK_STATUSES, t.status).cls}`} data-testid={`task-status-${t.id}`}>
                    {TASK_STATUSES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                  </select>
                </td>
                <td className="px-2 py-2" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-end gap-0.5">
                    <button onClick={() => onDuplicate(t)} className="p-1.5 text-slate-400 hover:text-sky-700 hover:bg-sky-50 rounded" title="Dupliquer (réutiliser les étapes)" data-testid={`task-duplicate-${t.id}`}><Copy className="w-3.5 h-3.5" /></button>
                    <button onClick={() => onDelete(t)} className="p-1.5 text-slate-400 hover:text-rose-700 hover:bg-rose-50 rounded" title="Supprimer" data-testid={`task-delete-${t.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </td>
              </tr>
            );
          })}
          {!tasks.length && (
            <tr><td colSpan={8}>
              <Empty icon={ListChecks} text={done ? "Aucune tâche terminée pour ces filtres." : "Aucune tâche ouverte pour ces filtres."} testId="task-empty">
                {!done && <Btn icon={Plus} onClick={onNew} testId="task-empty-new-btn">Nouvelle tâche</Btn>}
              </Empty>
            </td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
